import React, { useState, useCallback, useRef, useEffect } from 'react';

import type ItemList from '@this/domain/approve_item/item_list';
import {
  WORKFLOW_SELECTOR_FONT_SIZE,
  WORKFLOW_SELECTOR_LINE_HEIGHT,
  WorkflowSelectorArrow,
  workflowSelectorTriggerStyle
} from '@this/components/shared/workflow_selector_appearance';
import SelectApproveItemListList from './select_approveitemlist_list/select_approveitemlist_list';

interface Props {
  approveItemId: number;
  chargingDepartmentIds?: number[];
  defaultItemListName: string | undefined;
  onSelect: (itemList: ItemList | null) => void;
  tripedit?: boolean;
  workflowFormWidth: string;
}

interface TruncatableTextProps {
  text: string;
}

interface HTMLElementEvent<T extends EventTarget> extends Event {
  target: T;
}

const useTruncated = (
  text?: string | undefined | null
): [(node: HTMLDivElement | null) => void, boolean | undefined] => {
  const [truncated, setTruncated] = useState(false);
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      setTruncated(node.scrollWidth > node.offsetWidth);
    },
    [text]
  );
  return [ref, truncated];
};

const TruncatableText: React.FC<TruncatableTextProps> = ({ text }) => {
  const [ref, isTruncated] = useTruncated(text);
  const title = isTruncated ? { title: text } : {};

  return (
    <div
      ref={ref}
      {...title}
      style={{
        width: '100%',
        minWidth: 0,
        background: '#fff',
        fontSize: WORKFLOW_SELECTOR_FONT_SIZE,
        lineHeight: WORKFLOW_SELECTOR_LINE_HEIGHT,
        padding: 0,
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      }}
    >
      {text}
    </div>
  );
};

const SelectApproveItemList: React.FC<Props> = ({
  approveItemId,
  chargingDepartmentIds,
  defaultItemListName,
  onSelect,
  tripedit,
  workflowFormWidth
}) => {
  const [showList, setShowList] = useState(false);
  const [itemList, setItemList] = useState<ItemList | null>(null);
  const selectAreaRef = useRef<HTMLDivElement>(null);

  const handleClickEvent = useCallback((e: HTMLElementEvent<HTMLElement>) => {
    if (selectAreaRef.current && !selectAreaRef.current.contains(e.target as Node)) {
      setShowList(false);
    }
  }, []);

  useEffect(() => {
    const clickHandler = {
      handleEvent: (e: HTMLElementEvent<HTMLElement>) => {
        handleClickEvent(e);
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, [handleClickEvent]);

  const handleSelect = useCallback(
    (selectedItemList: ItemList | null) => {
      setShowList(false);
      setItemList(selectedItemList);
      onSelect(selectedItemList);
    },
    [onSelect]
  );

  const handleToggleShowList = useCallback(() => {
    setShowList(prev => !prev);
  }, []);

  const getDefaultItemListName = (): string => {
    if (defaultItemListName !== null && defaultItemListName !== undefined) {
      return defaultItemListName;
    }
    return '';
  };

  try {
    return (
      <div ref={selectAreaRef} style={{ margin: 0, width: workflowFormWidth || '420px' }}>
        <div
          onClick={handleToggleShowList}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleShowList();
            }
          }}
          role="button"
          tabIndex={0}
          style={{
            ...workflowSelectorTriggerStyle,
            minWidth: '50px',
            width: '100%',
            flexWrap: 'nowrap'
          }}
        >
          {itemList ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              <TruncatableText text={`${itemList.code}：${itemList.name}`} />
            </div>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }}>
              <TruncatableText text={getDefaultItemListName()} />
            </div>
          )}
          <WorkflowSelectorArrow open={showList} />
        </div>
        {showList && (
          <SelectApproveItemListList
            self={itemList}
            onSelect={handleSelect}
            approveItemId={approveItemId}
            chargingDepartmentIds={chargingDepartmentIds}
            tripedit={tripedit}
            workflowFormWidth={workflowFormWidth}
          />
        )}
      </div>
    );
  } catch (e) {
    return null;
  }
};

export default SelectApproveItemList;
